/* Resolves which announcement the header bar shows, from the store's
   announcement metaobject.

   `snippets/aqt-announcements-metaobject.liquid` has already done the path
   targeting server side and serialized every entry that can apply to this page
   as a <template>. What is left, and what cannot be done server side because
   pages are cached per URL, is picking the entry that matches THIS visitor's
   campaign: the live query string merged over `localStorage.attribution_data`
   (written by attribution.js / redirect-resolver.liquid).

   The bar the server rendered is the one a visitor with no campaign gets, so
   the common case is a signature match and this file changes nothing. */
(function () {
  'use strict';

  if (typeof customElements === 'undefined' || customElements.get('aqt-announcement-bar')) return;

  var ATTRIBUTION_STORAGE_KEY = 'attribution_data';
  var DISMISSED_STORAGE_KEY = 'dismissed_announcements';
  /* The attribution TTL. This used to mirror utmPersistenceTtlDays in
     channel-selling-plans-config.json, which was retired 2026-09-23; the bar
     owns the value outright now. The
     attribution store itself never expires, so a campaign bar would otherwise
     follow a visitor forever. */
  var ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var MAX_SLIDES = 6;

  /* ---------- trigger parsing and matching (ported from the Horizon theme) ---------- */

  function parseTriggers(raw) {
    if (raw == null || raw === '') return null;
    var value = String(raw).trim();
    if (!value) return null;
    try {
      var parsed = JSON.parse(value);
      // A json metafield can round-trip as a JSON string of a JSON string.
      if (typeof parsed === 'string') {
        var inner = parsed.trim();
        if (inner.charAt(0) === '{' || inner.charAt(0) === '[') {
          try {
            parsed = JSON.parse(inner);
          } catch (e) {
            /* keep the outer parse */
          }
        }
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length) {
        return parsed;
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  function lookupParam(params, key) {
    var lower = String(key).toLowerCase();
    for (var name in params) {
      if (!Object.prototype.hasOwnProperty.call(params, name)) continue;
      if (name.toLowerCase() !== lower) continue;
      var value = params[name];
      if (value != null && String(value).length) return String(value);
    }
    return '';
  }

  function valueMatchesPattern(pattern, value) {
    var p = String(pattern).trim();
    var v = String(value).trim();
    if (p === '*') return v.length > 0;
    if (p.indexOf('*') === -1) return p.toLowerCase() === v.toLowerCase();
    var parts = p.split('*').map(function (part) {
      return part.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    });
    return new RegExp('^' + parts.join('.*') + '$', 'i').test(v);
  }

  function valueMatchesRule(rule, actual) {
    if (Array.isArray(rule)) {
      return rule.some(function (one) {
        return valueMatchesPattern(one, actual);
      });
    }
    return valueMatchesPattern(rule, actual);
  }

  /* Every key must match (AND); a key's array of values is a set of
     alternatives (OR). Values accept `*` globs. */
  function triggersMatch(triggers, params) {
    if (!triggers) return true;
    var keys = Object.keys(triggers);
    if (!keys.length) return true;
    for (var i = 0; i < keys.length; i++) {
      var expected = triggers[keys[i]];
      if (expected == null) return false;
      if (!valueMatchesRule(expected, lookupParam(params, keys[i]))) return false;
    }
    return true;
  }

  /* ---------- the visitor's parameters ---------- */

  function urlParams() {
    var out = {};
    try {
      new URLSearchParams(window.location.search).forEach(function (value, key) {
        var trimmed = String(value).trim();
        if (trimmed) out[key] = trimmed;
      });
    } catch (e) {
      /* ignore */
    }
    return out;
  }

  function storedParams() {
    var out = {};
    try {
      var raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      if (!raw) return out;
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object' || Array.isArray(data)) return out;
      var capturedAt = Date.parse(data.utm_captured_at);
      // No timestamp means we cannot age it out, so treat it as expired.
      if (!capturedAt || Date.now() - capturedAt > ATTRIBUTION_TTL_MS) return out;
      for (var key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
        var value = String(data[key] == null ? '' : data[key]).trim();
        if (value) out[key] = value;
      }
    } catch (e) {
      /* ignore */
    }
    return out;
  }

  function normalizeKey(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /* Fills in only what is absent, so a real parameter always wins. */
  function withDerived(params) {
    function set(key, value) {
      if (value && !lookupParam(params, key)) params[key] = value;
    }

    /* attribution.js derives these from an Everflow link too, but it runs on
       DOMContentLoaded — after this — so on a first affiliate visit the values
       are not in storage yet. */
    var oid2 = lookupParam(params, 'oid2');
    var affid2 = lookupParam(params, 'affid2');
    if (oid2 && affid2) {
      set('utm_source', 'everflow');
      set('utm_medium', 'influencers');
      set('utm_campaign', affid2);
      set('utm_id', affid2);
    }

    /* The channel vocabulary of channel-selling-plans.js, so one entry keyed
       `{"channel":["affiliate"]}` covers affiliate traffic whichever
       utm_medium it reports (it changed to `influencers` in Aug 2026). */
    var source = normalizeKey(lookupParam(params, 'utm_source'));
    var medium = normalizeKey(lookupParam(params, 'utm_medium'));
    if (source === 'everflow' && (medium === 'influencers' || medium === 'affiliate')) {
      set('channel', 'affiliate');
    }

    return params;
  }

  /* ---------- dismissal ---------- */

  function dismissedHandles() {
    try {
      var raw = window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function persistDismissed(handles) {
    try {
      var merged = dismissedHandles();
      handles.forEach(function (handle) {
        if (handle && merged.indexOf(handle) === -1) merged.push(handle);
      });
      window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(merged));
    } catch (e) {
      /* ignore */
    }
  }

  function escapeAttribute(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  var AqtAnnouncementBar = class extends HTMLElement {
    connectedCallback() {
      try {
        this.resolve();
      } catch (e) {
        /* Never let a resolver failure leave the bar hidden. */
      } finally {
        document.documentElement.classList.remove('aqt-ann-resolving');
      }
    }

    getSlot() {
      return document.getElementById(this.dataset.slotId || '');
    }

    resolve() {
      var slot = this.getSlot();
      if (!slot) return;

      var templates = Array.prototype.slice.call(this.querySelectorAll('template[data-aqt-announcement]'));
      if (!templates.length) return;

      var params = withDerived(Object.assign({}, storedParams(), urlParams()));
      var nowSeconds = Math.floor(Date.now() / 1000);
      var dismissed = dismissedHandles();

      var candidates = [];
      templates.forEach(function (template, index) {
        var data = template.dataset;
        var start = parseInt(data.start, 10);
        var end = parseInt(data.end, 10);
        if (start && nowSeconds < start) return;
        if (end && nowSeconds > end) return;
        if (data.handle && dismissed.indexOf(data.handle) > -1) return;

        var triggers = parseTriggers(data.utmTriggers);
        candidates.push({
          template: template,
          handle: data.handle || '',
          tier: parseInt(data.pathTier, 10) || 0,
          targeted: data.targeted === 'true',
          triggers: triggers,
          keyCount: triggers ? Object.keys(triggers).length : 0,
          priority: parseInt(data.priority, 10) || 0,
          background: data.bgRgb || '',
          foreground: data.fgRgb || '',
          buttonBackground: data.btnBgRgb || '',
          buttonForeground: data.btnFgRgb || '',
          dismissible: data.dismissible === 'true',
          index: index
        });
      });

      /* `triggers` is null when the entry is flagged targeted but its
         utm_triggers could not be parsed (authored as an array, say). Matching
         it would show that entry to everyone AND suppress the default bar, so
         an entry we cannot read is an entry we do not show. */
      var matched = candidates.filter(function (candidate) {
        return candidate.targeted && candidate.triggers && triggersMatch(candidate.triggers, params);
      });

      var winners;
      if (matched.length) {
        /* A campaign match wins outright. Unlike the Horizon theme, one campaign
           entry existing does not black out cold traffic. These are only ever
           rendered here, so this ordering is the only one that applies. */
        winners = matched.sort(function (a, b) {
          if (b.tier !== a.tier) return b.tier - a.tier;
          if (b.priority !== a.priority) return b.priority - a.priority;
          if (b.keyCount !== a.keyCount) return b.keyCount - a.keyCount;
          return a.index - b.index;
        });
      } else {
        /* The default bar. This set is also rendered server side, and must be
           selected and ordered identically or the signature never matches and
           every load swaps the slot: Liquid takes only the most specific
           matching tier, in admin order. Priority orders campaigns, not
           defaults — Liquid cannot sort by it. */
        var defaults = candidates.filter(function (candidate) {
          return !candidate.targeted;
        });
        var bestTier = defaults.reduce(function (best, candidate) {
          return Math.max(best, candidate.tier);
        }, 0);
        winners = defaults
          .filter(function (candidate) {
            return candidate.tier === bestTier;
          })
          .sort(function (a, b) {
            return a.index - b.index;
          });
      }
      winners = winners.slice(0, MAX_SLIDES);

      var signature = winners
        .map(function (winner) {
          return winner.handle;
        })
        .join(',');
      var blocksTemplate = this.querySelector('template[data-aqt-announcement-blocks]');
      var useBlocks = false;

      if (!signature) {
        if (this.dataset.source === 'metaobject' || !blocksTemplate) {
          signature = this.dataset.source === 'metaobject' ? '__none__' : '__blocks__';
        } else {
          signature = '__blocks__';
          useBlocks = true;
        }
      }

      /* Already showing the right set — leave the DOM alone so Flickity keeps
         running, but still make sure the initial tabindex and the dismiss
         control are in place, since the server cannot render either. */
      if (signature === slot.dataset.aqtRendered) {
        this.stampTabindex(slot);
        this.mountDismiss(slot, winners);
        this.setCollapsed(slot.querySelectorAll('.announcement__slide, .scrolling-text__item').length === 0);
        return;
      }

      var slidesHtml = '';
      var count = 0;
      if (useBlocks) {
        slidesHtml = blocksTemplate.innerHTML;
        count = parseInt(blocksTemplate.dataset.count, 10) || 0;
      } else if (winners.length) {
        slidesHtml = winners
          .map(function (winner) {
            return winner.template.innerHTML;
          })
          .join('');
        count = winners.length;
      }

      slot.innerHTML = this.buildHtml(slidesHtml, count);
      slot.dataset.aqtRendered = signature;
      this.stampTabindex(slot);
      this.applyColors(winners.length ? winners[0] : null);
      this.mountDismiss(slot, winners);
      this.setCollapsed(count === 0);
    }

    /* An empty bar is not a blank bar: `.topbar-section` keeps its height and
       background with no content in it, so a page where nothing applies would
       show a strip of the section's colour. Collapsing has to be reversible,
       since a campaign can still be swapped in on a later resolve. */
    setCollapsed(isEmpty) {
      if (this.dataset.hasExtras === 'true') return;

      var sectionId = this.dataset.sectionId;
      var root = document.getElementById('shopify-section-' + sectionId);

      if (isEmpty) {
        if (root) root.style.display = 'none';
        document.documentElement.style.setProperty('--topbar-height', '0px');
        return;
      }

      var serverStyle = document.getElementById('AnnouncementEmpty-' + sectionId);
      if (serverStyle && serverStyle.parentNode) serverStyle.parentNode.removeChild(serverStyle);
      if (root) root.style.removeProperty('display');
      document.documentElement.style.removeProperty('--topbar-height');
    }

    /* Built as a string, then assigned in one go: the theme's <announcement-bar>
       memoizes Array.from(this.children) in its constructor, so an element
       created empty and appended to memoizes an empty list and Flickity never
       starts. Setting innerHTML parses the subtree before insertion, so the
       element upgrades with its slides already in place. */
    buildHtml(slidesHtml, count) {
      var sectionId = this.dataset.sectionId;
      if (this.dataset.layout === 'marquee') {
        var direction = this.dataset.direction || 'left';
        return (
          '<marquee-element id="Slider-' +
          escapeAttribute(sectionId) +
          '" class="scrolling-text scrolling-text--' +
          escapeAttribute(direction) +
          ' flex items-center w-full" data-speed="' +
          escapeAttribute(this.dataset.speed || '20') +
          '" data-direction="' +
          escapeAttribute(direction) +
          '">' +
          // MarqueeElement only clones when it has exactly one child element.
          '<div class="marquee with-dot h-full flex items-center shrink-0 whitespace-nowrap">' +
          slidesHtml +
          '</div></marquee-element>'
        );
      }

      var multiple = count > 1;
      var autoplay = this.dataset.autoplay === 'true';
      var html =
        '<announcement-bar id="Slider-' +
        escapeAttribute(sectionId) +
        '" class="announcement__slides' +
        (multiple ? ' flex justify-center w-full h-full flickity' : '') +
        '"' +
        (autoplay ? ' autoplay autoplay-speed="' + escapeAttribute(this.dataset.autoplaySpeed || '5') + '"' : '') +
        '>' +
        slidesHtml +
        '</announcement-bar>';

      var navTemplate = this.querySelector('template[data-aqt-announcement-nav]');
      if (navTemplate && this.dataset.showNavigation === 'true' && multiple) {
        html += navTemplate.innerHTML;
      }
      return html;
    }

    /* Flickity manages aria-hidden on its cells, and <announcement-bar> moves
       tabindex when the index changes — but only from the second change on, so
       the initial state is ours to set. */
    stampTabindex(slot) {
      if (this.dataset.layout === 'marquee') return;
      var slides = slot.querySelectorAll('.announcement__slide');
      if (slides.length < 2) return;
      Array.prototype.forEach.call(slides, function (slide, index) {
        var focusable = slide.querySelectorAll('button, [href]');
        Array.prototype.forEach.call(focusable, function (element) {
          if (index === 0) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    applyColors(winner) {
      var sectionId = this.dataset.sectionId;
      /* The server-rendered override belongs to the bar we just replaced. Drop
         it so the inline properties below are the only source of truth. */
      var serverStyle = document.getElementById('AnnouncementColors-' + sectionId);
      if (serverStyle && serverStyle.parentNode) serverStyle.parentNode.removeChild(serverStyle);

      var root = document.getElementById('shopify-section-' + sectionId);
      if (!root) return;

      var background = winner ? winner.background : '';
      var foreground = winner ? winner.foreground : '';

      if (background) {
        root.style.setProperty('--color-background', background);
        /* .topbar-section paints --gradient-background after background-color,
           so setting only the latter changes nothing. */
        root.style.setProperty('--gradient-background', 'rgb(' + background + ')');
      } else {
        root.style.removeProperty('--color-background');
        root.style.removeProperty('--gradient-background');
      }

      if (foreground) {
        root.style.setProperty('--color-foreground', foreground);
      } else {
        root.style.removeProperty('--color-foreground');
      }

      /* Derived in Liquid, where the colour-contrast filters live, so the
         button label stays readable on whatever colour the campaign picked. */
      var buttonBackground = winner ? winner.buttonBackground : '';
      var buttonForeground = winner ? winner.buttonForeground : '';

      if (buttonBackground) {
        root.style.setProperty('--color-button-background', buttonBackground);
        root.style.setProperty('--color-button-border', buttonBackground);
      } else {
        root.style.removeProperty('--color-button-background');
        root.style.removeProperty('--color-button-border');
      }

      if (buttonForeground) {
        root.style.setProperty('--color-button-text', buttonForeground);
      } else {
        root.style.removeProperty('--color-button-text');
      }
    }

    mountDismiss(slot, winners) {
      var existing = slot.querySelector('[data-aqt-announcement-dismiss]');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var template = this.querySelector('template[data-aqt-announcement-dismiss]');
      if (!template || !winners.length || !winners[0].dismissible) return;

      var wrapper = document.createElement('div');
      wrapper.innerHTML = template.innerHTML;
      var button = wrapper.querySelector('[data-aqt-announcement-dismiss]');
      if (!button) return;

      var handles = winners.map(function (winner) {
        return winner.handle;
      });
      var self = this;
      button.addEventListener('click', function () {
        persistDismissed(handles);
        self.resolve();
      });
      // Outside <announcement-bar> so it is never a Flickity cell.
      slot.appendChild(button);
    }
  };

  customElements.define('aqt-announcement-bar', AqtAnnouncementBar);
})();
